import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import { AuthProvider } from "@/lib/auth-context";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminShell from "@/components/AdminShell";
import PortalShell from "@/components/PortalShell";
import Home from "@/pages/Home";
import Programs from "@/pages/Programs";
import Platform from "@/pages/Platform";
import Pricing from "@/pages/Pricing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import About from "@/pages/About";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Whitepaper from "@/pages/Whitepaper";
import Resources from "@/pages/Resources";
import HowItWorks from "@/pages/HowItWorks";
import Demo from "@/pages/Demo";
import Watch from "@/pages/Watch";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import Vendors from "@/pages/Vendors";
import VendorApply from "@/pages/VendorApply";
import MagicLinkCallback from "@/pages/MagicLinkCallback";
import QuickAction from "@/pages/QuickAction";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import RequestAccess from "@/pages/RequestAccess";
import PublicScreener from "@/pages/PublicScreener";
import Portal from "@/pages/Portal";
import PortalIntelligence from "@/pages/portal/PortalIntelligence";
import PortalScreeningInsights from "@/pages/portal/PortalScreeningInsights";
import PortalOpportunityScanner from "@/pages/portal/PortalOpportunityScanner";
import PortalLeakage from "@/pages/portal/analytics/PortalLeakage";
import PortalProgramPerformance from "@/pages/portal/analytics/PortalProgramPerformance";
import PortalVendorPerformance from "@/pages/portal/analytics/PortalVendorPerformance";
import PortalCohorts from "@/pages/portal/analytics/PortalCohorts";
import PortalConsumerProfile from "@/pages/portal/PortalConsumerProfile";
import PortalCatalog from "@/pages/portal/PortalCatalog";
import PortalProgramLaunch from "@/pages/portal/PortalProgramLaunch";
import PortalScreeners from "@/pages/portal/PortalScreeners";
import PortalConsumers from "@/pages/portal/PortalConsumers";
import PortalMarketing from "@/pages/portal/PortalMarketing";
import PortalCampaigns from "@/pages/portal/PortalCampaigns";
import PortalCampaignEditor from "@/pages/portal/PortalCampaignEditor";
import PortalVendors from "@/pages/portal/PortalVendors";
import PortalTraining from "@/pages/portal/PortalTraining";
import PortalSettings from "@/pages/portal/PortalSettings";
import PortalBilling from "@/pages/portal/PortalBilling";
import PortalBillingReturn from "@/pages/portal/PortalBillingReturn";
import PortalServiceRequests from "@/pages/portal/PortalServiceRequests";
import PortalThinkTank from "@/pages/portal/PortalThinkTank";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminDemos from "@/pages/AdminDemos";
import AdminVendors from "@/pages/AdminVendors";
import AdminServices from "@/pages/admin/AdminServices";
import AdminPrograms from "@/pages/admin/AdminPrograms";
import AdminStripeConnect from "@/pages/admin/AdminStripeConnect";
import AdminFounderDashboard from "@/pages/admin/AdminFounderDashboard";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminInvites from "@/pages/admin/AdminInvites";
import AdminProcurement from "@/pages/admin/AdminProcurement";
import AdminWorkflowEngine from "@/pages/admin/AdminWorkflowEngine";
import AdminWorkflowDesigner from "@/pages/admin/AdminWorkflowDesigner";
import AdminWorkflowAnalytics from "@/pages/admin/AdminWorkflowAnalytics";
import AdminWorkflowSandbox from "@/pages/admin/AdminWorkflowSandbox";
import PublicCheckout from "@/pages/public/PublicCheckout";
import CheckoutSuccess from "@/pages/public/CheckoutSuccess";
import CheckoutCancel from "@/pages/public/CheckoutCancel";
import ClinicsLikeYours from "@/pages/public/ClinicsLikeYours";
import PortalPayments from "@/pages/portal/PortalPayments";
import AdminProgramDetail from "@/pages/admin/AdminProgramDetail";
import AdminKnowledgeBase from "@/pages/admin/knowledge-base/AdminKnowledgeBase";
import AdminKnowledgeBaseDatabase from "@/pages/admin/knowledge-base/AdminKnowledgeBaseDatabase";
import AdminKnowledgeBaseRecord from "@/pages/admin/knowledge-base/AdminKnowledgeBaseRecord";
import AdminKnowledgeBaseReports from "@/pages/admin/knowledge-base/AdminKnowledgeBaseReports";
import AdminKnowledgeBaseReport from "@/pages/admin/knowledge-base/AdminKnowledgeBaseReport";
import AdminScreeners from "@/pages/admin/AdminScreeners";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminCampaignManager from "@/pages/admin/AdminCampaignManager";
import FounderOS from "@/pages/admin/FounderOS";
import OpportunityEngine from "@/pages/admin/OpportunityEngine";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPlatformEconomics from "@/pages/admin/AdminPlatformEconomics";
import AdminPlatformStrategies from "@/pages/admin/AdminPlatformStrategies";
import AdminTodos from "@/pages/admin/AdminTodos";
import AdminVendorDirectory from "@/pages/admin/AdminVendorDirectory";
import AdminTraining from "@/pages/admin/AdminTraining";
import AdminWhitepapers from "@/pages/admin/AdminWhitepapers";
import AdminClinics from "@/pages/admin/AdminClinics";
import AdminClinicRoster from "@/pages/admin/AdminClinicRoster";
import AdminProgramRequests from "@/pages/admin/AdminProgramRequests";
import AdminVendorIntake from "@/pages/admin/AdminVendorIntake";
import AdminDemoAccess from "@/pages/admin/AdminDemoAccess";
import AdminDemoVideo from "@/pages/admin/AdminDemoVideo";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminWebsiteAnalytics from "@/pages/admin/AdminWebsiteAnalytics";
import AdminServiceRequests from "@/pages/admin/AdminServiceRequests";
import AdminThinkTankDigest from "@/pages/admin/AdminThinkTankDigest";
import AdminClinicAdoption from "@/pages/admin/AdminClinicAdoption";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminFutureConsiderations from "@/pages/admin/AdminFutureConsiderations";
import AdminRoiCalculator from "@/pages/admin/AdminRoiCalculator";
import AdminMarketingFlyer from "@/pages/admin/AdminMarketingFlyer";
import DemoAccess from "@/pages/DemoAccess";
import InvoicesList from "@/pages/workflow/InvoicesList";
import VendorShell from "@/components/VendorShell";
import VendorLogin from "@/pages/vendor/VendorLogin";
import VendorHome from "@/pages/vendor/VendorHome";
import VendorProfile from "@/pages/vendor/VendorProfile";
import VendorOrders from "@/pages/vendor/VendorOrders";
import VendorDispatches from "@/pages/vendor/VendorDispatches";
import VendorServices from "@/pages/vendor/VendorServices";
import VendorAnalytics from "@/pages/vendor/VendorAnalytics";
import VendorMessages from "@/pages/vendor/VendorMessages";
import PortalMessages from "@/pages/portal/PortalMessages";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import PortalVendorCatalog from "@/pages/portal/PortalVendorCatalog";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminVendorServices from "@/pages/admin/AdminVendorServices";
import AdminSystemHealth from "@/pages/admin/AdminSystemHealth";
import ConsumerSetup from "@/pages/ConsumerSetup";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { trackPageview } from "@/lib/analytics";

function RouteTracker() {
  const loc = useLocation();
  useEffect(() => {
    trackPageview(loc.pathname, loc.search);
  }, [loc.pathname, loc.search]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RouteTracker />
          <Routes>
            {/* Auth entry points render outside the marketing Layout so they */}
            {/* stay discreet (no marketing nav / footer leaking onto a stealth */}
            {/* login surface). */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/vendor/login" element={<VendorLogin />} />
            <Route path="/watch/:token" element={<Watch />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/programs" element={<Programs />} />
              <Route path="/platform" element={<Platform />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/resources/cost-of-cash-pay-programs" element={<Whitepaper />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/vendors" element={<Vendors />} />
              <Route path="/vendors/apply" element={<VendorApply />} />
              <Route path="/login" element={<Login />} />
              <Route path="/auth/link" element={<MagicLinkCallback />} />
              <Route path="/buy/:slug" element={<PublicCheckout />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancel" element={<CheckoutCancel />} />
              <Route path="/clinics-like-yours" element={<ClinicsLikeYours />} />
              <Route path="/clinics-like-you" element={<Navigate to="/clinics-like-yours" replace />} />
              <Route path="/q/:token" element={<QuickAction />} />
              <Route path="/request-access" element={<RequestAccess />} />
              <Route path="/c/setup/:token" element={<ConsumerSetup />} />
              <Route path="/demo/access/:token" element={<DemoAccess />} />
              <Route path="/s/:slug" element={<PublicScreener />} />

              {/* Clinic Portal — sidebar shell wraps all /portal/* routes */}
              <Route
                element={
                  <ProtectedRoute role="clinic">
                    <PortalShell />
                  </ProtectedRoute>
                }
              >
                <Route path="/portal" element={<PortalIntelligence />} />
                <Route path="/portal/insights" element={<PortalScreeningInsights />} />
                <Route path="/portal/legacy" element={<Portal />} />
                <Route path="/portal/opportunity-scanner" element={<PortalOpportunityScanner />} />
                <Route path="/portal/analytics/leakage" element={<PortalLeakage />} />
                <Route path="/portal/analytics/programs" element={<PortalProgramPerformance />} />
                <Route path="/portal/analytics/vendors" element={<PortalVendorPerformance />} />
                <Route path="/portal/analytics/cohorts" element={<PortalCohorts />} />
                <Route path="/portal/catalog" element={<PortalCatalog />} />
                <Route path="/portal/launch" element={<PortalProgramLaunch />} />
                <Route path="/portal/payments" element={<PortalPayments />} />
                <Route path="/portal/screeners" element={<PortalScreeners />} />
                <Route path="/portal/consumers" element={<PortalConsumers />} />
                <Route path="/portal/consumers/:id" element={<PortalConsumerProfile />} />
                <Route path="/portal/marketing" element={<PortalMarketing />} />
                <Route path="/portal/marketing/campaigns" element={<PortalCampaigns />} />
                <Route path="/portal/marketing/campaigns/new" element={<PortalCampaignEditor />} />
                <Route path="/portal/marketing/campaigns/:id" element={<PortalCampaignEditor />} />
                <Route path="/portal/vendors" element={<PortalVendors />} />
                <Route path="/portal/training" element={<PortalTraining />} />
                <Route path="/portal/settings" element={<PortalSettings />} />
                <Route path="/portal/billing" element={<PortalBilling />} />
                <Route path="/portal/billing/return" element={<PortalBillingReturn />} />
                <Route path="/portal/service-requests" element={<PortalServiceRequests />} />
                <Route path="/portal/think-tank" element={<PortalThinkTank />} />
                <Route path="/portal/messages" element={<PortalMessages />} />
                <Route path="/portal/documents" element={<PortalDocuments />} />
                <Route path="/portal/vendor-catalog" element={<PortalVendorCatalog />} />
                <Route path="/portal/invoices" element={<InvoicesList portal="clinic" />} />
              </Route>

              {/* Vendor Portal — separate shell, only role=vendor */}
              <Route
                element={
                  <ProtectedRoute role="vendor">
                    <VendorShell />
                  </ProtectedRoute>
                }
              >
                <Route path="/vendor" element={<VendorHome />} />
                <Route path="/vendor/dashboard" element={<VendorHome />} />
                <Route path="/vendor/orders" element={<VendorOrders />} />
                <Route path="/vendor/dispatches" element={<VendorDispatches />} />
                <Route path="/vendor/services" element={<VendorServices />} />
                <Route path="/vendor/analytics" element={<VendorAnalytics />} />
                <Route path="/vendor/messages" element={<VendorMessages />} />
                <Route path="/vendor/payments" element={<PortalPayments />} />
                <Route path="/vendor/profile" element={<VendorProfile />} />
                <Route path="/vendor/invoices" element={<InvoicesList portal="vendor" />} />
              </Route>

              {/* Admin — sidebar shell wraps all /admin/* tool routes */}
              <Route
                element={
                  <ProtectedRoute role="admin">
                    <AdminShell />
                  </ProtectedRoute>
                }
              >
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/execute" element={<FounderOS />} />
                <Route path="/admin/opportunity" element={<OpportunityEngine />} />
                <Route path="/admin/todos" element={<AdminTodos />} />
                <Route path="/admin/vendors-directory" element={<AdminVendorDirectory />} />
                <Route path="/admin/demos" element={<AdminDemos />} />
                <Route path="/admin/vendors" element={<AdminVendors />} />
                <Route path="/admin/services" element={<AdminServices />} />
                <Route path="/admin/programs" element={<AdminPrograms />} />
                <Route path="/admin/programs/:id" element={<AdminProgramDetail />} />
                <Route path="/admin/stripe-connect" element={<AdminStripeConnect />} />
                <Route path="/admin/founder-dashboard" element={<AdminFounderDashboard />} />
                <Route path="/admin/payments" element={<AdminPayments />} />
                <Route path="/admin/invites" element={<AdminInvites />} />
                <Route path="/admin/procurement" element={<AdminProcurement />} />
                <Route path="/admin/workflow-engine" element={<AdminWorkflowEngine />} />
                <Route path="/admin/workflow-engine/designer/:definitionId" element={<AdminWorkflowDesigner />} />
                <Route path="/admin/workflow-engine/analytics" element={<AdminWorkflowAnalytics />} />
                <Route path="/admin/workflow-engine/sandbox/:runId" element={<AdminWorkflowSandbox />} />
                <Route path="/admin/packages" element={<Navigate to="/admin/programs" replace />} />
                <Route path="/admin/screeners" element={<AdminScreeners />} />
                <Route path="/admin/marketing" element={<AdminMarketing />} />
                <Route path="/admin/campaign-manager" element={<AdminCampaignManager />} />
                <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
                <Route path="/admin/platform-economics" element={<AdminPlatformEconomics />} />
                <Route path="/admin/platform-strategies" element={<AdminPlatformStrategies />} />
                <Route path="/admin/training" element={<AdminTraining />} />
                <Route path="/admin/whitepapers" element={<AdminWhitepapers />} />
                <Route path="/admin/clinics" element={<AdminClinics />} />
                <Route path="/admin/clinic-roster" element={<AdminClinicRoster />} />
                <Route path="/admin/program-requests" element={<AdminProgramRequests />} />
                <Route path="/admin/vendor-intake" element={<AdminVendorIntake />} />
                <Route path="/admin/demo-access" element={<AdminDemoAccess />} />
                <Route path="/admin/demo-video" element={<AdminDemoVideo />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/website-analytics" element={<AdminWebsiteAnalytics />} />
                <Route path="/admin/service-requests" element={<AdminServiceRequests />} />
                <Route path="/admin/think-tank-digest" element={<AdminThinkTankDigest />} />
                <Route path="/admin/clinic-adoption" element={<AdminClinicAdoption />} />
                <Route path="/admin/blog" element={<AdminBlog />} />
                <Route path="/admin/email" element={<AdminEmail />} />
                <Route path="/admin/future-considerations" element={<AdminFutureConsiderations />} />
                <Route path="/admin/roi-calculator" element={<AdminRoiCalculator />} />
                <Route path="/admin/marketing-flyer" element={<AdminMarketingFlyer />} />
                <Route path="/admin/messages" element={<AdminMessages />} />
                <Route path="/admin/documents" element={<AdminDocuments />} />
                <Route path="/admin/vendor-services" element={<AdminVendorServices />} />
                <Route path="/admin/system-health" element={<AdminSystemHealth />} />
                <Route path="/admin/knowledge-base" element={<AdminKnowledgeBase />} />
                <Route path="/admin/knowledge-base/reports" element={<AdminKnowledgeBaseReports />} />
                <Route path="/admin/knowledge-base/reports/:reportId" element={<AdminKnowledgeBaseReport />} />
                <Route path="/admin/knowledge-base/:dbKey" element={<AdminKnowledgeBaseDatabase />} />
                <Route path="/admin/knowledge-base/:dbKey/:recordId" element={<AdminKnowledgeBaseRecord />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
